/**
 * Centralized environment configuration
 * All environment variables are accessed through this file for consistency and type safety.
 * 
 * NEXT_PUBLIC_ prefix is required for variables that need to be available on the client side.
 * Most variables have production defaults. Required deployment variables throw when missing.
 */

const requireEnv = (name: string, value: string | undefined): string => {
  if (!value) {
    throw new Error(`${name} must be configured for this Netlify context.`);
  }
  return value;
};

export const ENV = {
  // API Endpoints
  API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL || 'https://api.varsitytutors.com',
  EVENTS_API_URL: process.env.NEXT_PUBLIC_EVENTS_API_URL || 'https://events.varsitytutors.com',
  
  // Site URLs
  SITE_BASE_URL: process.env.NEXT_PUBLIC_SITE_BASE_URL || 'https://www.varsitytutors.com',
  
  // Third-Party IDs
  GTM_ID: process.env.NEXT_PUBLIC_GTM_ID || '',
  ONETRUST_DOMAIN: process.env.NEXT_PUBLIC_ONETRUST_DOMAIN || '7eaac65c-a7a6-48de-a840-1f0f78d1bfbf',
  META_PIXEL_ID: process.env.NEXT_PUBLIC_META_PIXEL_ID || '',  // Optional - empty means disabled
  
  // PostHog — empty means disabled
  POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY || '',
  MARKETING_ATTRIBUTION_SCRIPT_URL: requireEnv(
    'NEXT_PUBLIC_MARKETING_ATTRIBUTION_SCRIPT_URL',
    process.env.NEXT_PUBLIC_MARKETING_ATTRIBUTION_SCRIPT_URL,
  ),

  // CORS - Allowed origins for API requests (comma-separated in env var)
  ALLOWED_ORIGINS: (process.env.NEXT_PUBLIC_ALLOWED_ORIGINS || 'https://www.varsitytutors.com,https://varsitytutors.com')
    .split(',')
    .map(origin => origin.trim()),
} as const;

// Static asset URLs (same across all environments)
const ASSETS_BASE_URL = 'https://assets2.varsitytutors.com';
const IMGIX_BASE_URL = 'https://llt.imgix.net';

// Derived URLs for convenience
export const URLS = {
  // API endpoints
  CRM_API: `${ENV.API_BASE_URL}/v1/contact/requests`,
  SCHEDULE_CALLBACK_API: `${ENV.API_BASE_URL}/leads-hub/schedule-callback`,
  PHONE_LOOKUP_API: `${ENV.API_BASE_URL}/vtwa/v6/locale_directory/lookup`,
  EVENTS_API: `${ENV.EVENTS_API_URL}/v2/pages/interaction`,
  
  // Site URLs
  TERMS_OF_USE: `${ENV.SITE_BASE_URL}/terms-of-use`,
  DEFAULT_REFERER: `${ENV.SITE_BASE_URL}/`,
  DEFAULT_LANDING_PAGE: `${ENV.SITE_BASE_URL}/`,
  
  // Asset URLs (hardcoded - same across all environments)
  TILE_IMAGE: `${ASSETS_BASE_URL}/llt-atlas/images/favicons/vt/msapplication-tile.png`,
  OG_IMAGE: `${IMGIX_BASE_URL}/vt/vt-logo-B-color.png?ixlib=rb-3.3.1&fit=max&auto=format%2Ccompress&w=1200&h=200`,
} as const;

// Type exports for better TypeScript support
export type EnvConfig = typeof ENV;
export type UrlConfig = typeof URLS;
